import { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { getCompanySpCreds, getMyCompany, patchMyCompany, testCompanySpCreds, upsertCompanySpCreds, deleteCompanySpCreds, recomputeTaskSeq, getProjectDiagnostics, http } from "../lib/api";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import { Building2, Cloud, CheckCircle2, AlertTriangle, Trash2, Wrench, Loader2, Key, Copy, Plus, FileBox, RefreshCw, ExternalLink, Stethoscope } from "lucide-react";

export default function Settings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [company, setCompany] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const [creds, setCreds] = useState(null);
  const [credsForm, setCredsForm] = useState({ tenant_id: "", client_id: "", client_secret: "", display_name: "" });
  const [savingCreds, setSavingCreds] = useState(false);
  const [testingCreds, setTestingCreds] = useState(false);
  const [editingCreds, setEditingCreds] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [c, cr] = await Promise.all([getMyCompany(), getCompanySpCreds().catch(() => null)]);
      setCompany(c);
      setCompanyName(c?.name || "");
      if (cr?.credentials) {
        setCreds(cr.credentials);
        setCredsForm({
          tenant_id: cr.credentials.tenant_id || "",
          client_id: cr.credentials.client_id || "",
          client_secret: "",
          display_name: cr.credentials.display_name || "",
        });
      } else {
        setCreds(null);
        setEditingCreds(true);
      }
    } catch (err) {
      // Non-fatal: settings page works without SP creds. Log for ops/dev visibility.
      console.warn("Settings refresh: failed to load company/sp creds", err);
    }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const onSaveCompany = async (e) => {
    e.preventDefault();
    try { await patchMyCompany({ name: companyName }); toast.success(t("success")); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onSaveCreds = async (e) => {
    e.preventDefault();
    setSavingCreds(true);
    try {
      const payload = {
        tenant_id: credsForm.tenant_id.trim(),
        client_id: credsForm.client_id.trim(),
        display_name: credsForm.display_name?.trim() || null,
      };
      if (credsForm.client_secret) payload.client_secret = credsForm.client_secret;
      const r = await upsertCompanySpCreds(payload);
      setCreds(r);
      setEditingCreds(false);
      setCredsForm((p) => ({ ...p, client_secret: "" }));
      toast.success(t("company_sp_creds_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSavingCreds(false); }
  };

  const onTestCreds = async () => {
    setTestingCreds(true);
    try {
      const r = await testCompanySpCreds();
      if (r.ok) toast.success(r.message);
      else toast.error(r.message);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setTestingCreds(false); }
  };

  const onDeleteCreds = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteCompanySpCreds(); toast.success(t("success")); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  if (loading) return <div className="px-12 py-12 text-sm text-zinc-500">{t("loading")}</div>;

  return (
    <div className="px-8 lg:px-12 py-10 max-w-3xl space-y-8" data-testid="settings-page">
      <div>
        <div className="cs-label">organisation</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">{t("nav_settings")}</h1>
      </div>

      {/* Company */}
      <form onSubmit={onSaveCompany} className="bg-white border border-zinc-200 rounded-md p-6 space-y-4" data-testid="company-form">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-zinc-500" />
          <h2 className="font-heading text-lg font-semibold">{t("company_info")}</h2>
        </div>
        <div>
          <label className="cs-label block mb-1.5">{t("company_name")}</label>
          <input type="text" value={companyName} disabled={!isAdmin} onChange={(e) => setCompanyName(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-100" data-testid="company-name-input" />
        </div>
        {isAdmin && <button type="submit" className="px-4 py-2 bg-cs-accent text-white rounded-md text-sm font-semibold hover:bg-cs-accentHover" data-testid="company-save-btn">{t("save")}</button>}
      </form>

      {/* SharePoint creds */}
      <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-4" data-testid="sp-creds-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cloud size={16} className="text-cs-blueprint" />
            <h2 className="font-heading text-lg font-semibold">{t("company_sp_creds_title")}</h2>
          </div>
          {creds ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-cs-success">
              <CheckCircle2 size={14} /> {t("configured")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-amber-600">
              <AlertTriangle size={14} /> {t("not_configured")}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500">{t("company_sp_creds_subtitle")}</p>

        {!editingCreds && creds ? (
          <div className="space-y-2 font-mono text-xs">
            <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
              <span className="cs-label">tenant_id</span>
              <span className="text-zinc-900">{creds.tenant_id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="cs-label">client_id</span>
              <span className="text-zinc-900">{creds.client_id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="cs-label">client_secret</span>
              <span className="text-zinc-400">•••••• ({creds.has_client_secret ? "salvo" : "vazio"})</span>
            </div>
            {creds.display_name && (
              <div className="flex items-center justify-between">
                <span className="cs-label">label</span>
                <span className="text-zinc-900">{creds.display_name}</span>
              </div>
            )}
            {creds.last_validated_at && (
              <div className="flex items-center justify-between">
                <span className="cs-label">último teste</span>
                <span className="text-zinc-500">{creds.last_validated_at.slice(0, 16).replace("T", " ")}</span>
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                <button onClick={onTestCreds} disabled={testingCreds} className="px-3 py-1.5 border border-zinc-300 rounded-md text-xs hover:bg-zinc-50 disabled:opacity-60" data-testid="sp-creds-test-btn">
                  {testingCreds ? "..." : t("test_connection")}
                </button>
                <button onClick={() => setEditingCreds(true)} className="px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs font-semibold hover:bg-zinc-800" data-testid="sp-creds-edit-btn">
                  {t("edit_credentials")}
                </button>
                <button onClick={onDeleteCreds} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 rounded" data-testid="sp-creds-delete-btn">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ) : (
          isAdmin && (
            <form onSubmit={onSaveCreds} className="space-y-3 border-t border-zinc-100 pt-4">
              <div>
                <label className="cs-label block mb-1.5">{t("tenant_id")}</label>
                <input type="text" required value={credsForm.tenant_id} onChange={(e) => setCredsForm({ ...credsForm, tenant_id: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="sp-creds-tenant-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("client_id")}</label>
                <input type="text" required value={credsForm.client_id} onChange={(e) => setCredsForm({ ...credsForm, client_id: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="sp-creds-client-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("client_secret")}</label>
                <input type="password" required={!creds?.has_client_secret} value={credsForm.client_secret} onChange={(e) => setCredsForm({ ...credsForm, client_secret: e.target.value })}
                  placeholder={creds?.has_client_secret ? "•••••• (mantém atual se vazio)" : ""}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="sp-creds-secret-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">Label (opcional)</label>
                <input type="text" value={credsForm.display_name} onChange={(e) => setCredsForm({ ...credsForm, display_name: e.target.value })}
                  placeholder="GT Strategy M365"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="sp-creds-label-input" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                {creds && <button type="button" onClick={() => { setEditingCreds(false); setCredsForm((p) => ({ ...p, client_secret: "" })); }} className="px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-100 rounded" data-testid="sp-creds-cancel-btn">{t("cancel")}</button>}
                <button type="submit" disabled={savingCreds} className="px-4 py-2 bg-cs-accent text-white rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60" data-testid="sp-creds-save-btn">
                  {savingCreds ? "..." : t("save_credentials")}
                </button>
              </div>
            </form>
          )
        )}
      </div>

      {isAdmin && <ApiTokensCard />}
      {isAdmin && <ProjectDiagnosticsCard />}
      {isAdmin && <MaintenanceCard isSuperAdmin={!!user?.is_super_admin} />}
    </div>
  );
}

/**
 * ProjectDiagnosticsCard — answers "why doesn't project X appear?".
 *
 * Reads /api/admin/diagnostics/projects, which cross-checks every project
 * against its company, its versions (and whether any produced tasks) and the
 * share links that expose it. Built after production reports of missing
 * projects, whose cause is almost always company scoping or versions that
 * imported zero tasks.
 */
function ProjectDiagnosticsCard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try { setData(await getProjectDiagnostics()); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  };

  const rows = (data?.projects || []).filter((p) => {
    if (onlyIssues && !p.issues.length) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (p.project_name || "").toLowerCase().includes(q) || (p.company_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="settings-diagnostics-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Stethoscope size={16} className="text-cs-blueprint" />
          <h2 className="font-heading text-lg font-semibold">{t("diag_title")}</h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cs-blueprint text-white rounded-md text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
          data-testid="diag-run-btn"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {t("diag_run")}
        </button>
      </div>
      <p className="text-xs text-zinc-500 mt-2">{t("diag_help")}</p>

      {data && (
        <div className="mt-4 space-y-3" data-testid="diag-results">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ["diag_total", data.summary.total],
              ["diag_no_ready", data.summary.without_ready_version],
              ["diag_other_company", data.summary.outside_active_company],
              ["diag_no_link", data.summary.not_in_any_link],
            ].map(([key, value]) => (
              <div key={key} className="border border-zinc-200 rounded-md p-2.5" data-testid={`diag-stat-${key}`}>
                <div className="cs-label !text-[10px]">{t(key)}</div>
                <div className="font-mono text-lg font-semibold text-zinc-900">{value}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("diag_search_placeholder")}
              className="flex-1 min-w-[180px] px-3 py-1.5 border border-zinc-300 rounded-md text-xs"
              data-testid="diag-search-input"
            />
            <label className="inline-flex items-center gap-1.5 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} className="rounded text-cs-accent" data-testid="diag-only-issues" />
              {t("diag_only_issues")}
            </label>
          </div>

          <div className="space-y-2 max-h-[420px] overflow-y-auto scrollbar-thin">
            {rows.length === 0 && <div className="text-xs text-zinc-500 font-mono">{t("diag_empty")}</div>}
            {rows.map((p) => (
              <div key={p.project_id} className="border border-zinc-200 rounded-md p-3" data-testid={`diag-row-${p.project_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-sm text-zinc-900">{p.project_name}</span>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{p.company_name || "—"}</span>
                  {!p.in_active_company && (
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{t("diag_badge_other_company")}</span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-zinc-500 mt-1">
                  {p.versions_total} {t("diag_versions")} · {p.versions_ready} {t("diag_with_tasks")} · {p.tasks_total} {t("tasks")} ·{" "}
                  {t("diag_served")}: {p.latest_served_version ? `v${p.latest_served_version.version} · ${p.latest_served_version.name}` : "—"} ·{" "}
                  {p.share_links.length} {t("diag_links")}
                </div>
                {p.issues.map((issue, i) => (
                  <div key={i} className="text-[11px] text-amber-800 mt-1">⚠ {issue}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ApiTokensCard — create / list / revoke Power BI API tokens for the active
 * company. The full token is shown ONCE at creation and never again — admins
 * must copy it immediately. Subsequent views only show a masked form.
 */
function ApiTokensCard() {
  const { t } = useI18n();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get("/v1/auth/tokens");
      setTokens(r.data.tokens || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await http.post("/v1/auth/tokens", { name: newName.trim() || undefined });
      setJustCreated(r.data);
      setNewName("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("token_confirm_revoke"))) return;
    try {
      await http.delete(`/v1/auth/tokens/${id}`);
      toast.success(t("token_revoked"));
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const copyToken = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(t("token_copied"));
    } catch (e) {
      // clipboard blocked in iframe; user can select manually
      logger.debug("clipboard write failed", e);
      toast.message(t("token_copied"));
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="settings-api-tokens-card">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <Key size={18} className="text-cs-blueprint" />
        <div>
          <h3 className="font-heading text-base font-semibold">{t("api_tokens_title")}</h3>
          <p className="text-[11px] text-zinc-500 font-mono">{t("api_tokens_subtitle")}</p>
        </div>
      </div>

      {justCreated && (
        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="api-token-revealed">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="cs-label text-amber-900">{t("token_created_label")}</div>
              <p className="text-xs text-amber-900 mt-1">{t("token_save_now_help")}</p>
              <div className="mt-3 bg-white border border-amber-300 rounded p-2 font-mono text-xs break-all" data-testid="api-token-value">
                {justCreated.token}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <button onClick={() => copyToken(justCreated.token)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded hover:bg-zinc-800" data-testid="api-token-copy">
                <Copy size={11} /> {t("copy")}
              </button>
              <button onClick={() => setJustCreated(null)} className="text-[11px] text-amber-700 hover:underline" data-testid="api-token-dismiss">
                {t("dismiss")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t("token_name_placeholder")}
          className="flex-1 px-3 py-2 border border-zinc-200 rounded-md text-sm"
          data-testid="api-token-name-input"
        />
        <button onClick={create} disabled={creating} className="inline-flex items-center gap-1.5 px-3 py-2 bg-cs-accent text-white text-xs font-semibold rounded-md hover:bg-cs-accentHover disabled:opacity-60" data-testid="api-token-create-btn">
          {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          {t("token_create_btn")}
        </button>
      </div>

      <div className="mt-5 border-t border-zinc-100 pt-4">
        <div className="cs-label mb-2">{t("token_list_title")}</div>
        {loading ? (
          <div className="text-xs text-zinc-500">{t("loading")}</div>
        ) : tokens.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">{t("token_list_empty")}</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {tokens.map((tk) => (
              <li key={tk.id} className={`flex items-center gap-3 py-2 ${tk.revoked ? "opacity-50" : ""}`} data-testid={`api-token-row-${tk.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{tk.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">
                    {tk.token_masked} · {tk.revoked ? t("token_status_revoked") : t("token_status_active")} · {t("token_created_at")} {(tk.created_at || "").slice(0, 10)}
                    {tk.last_used_at && ` · ${t("token_last_used")} ${tk.last_used_at.slice(0, 10)}`}
                  </div>
                </div>
                {!tk.revoked && (
                  <button onClick={() => revoke(tk.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`api-token-revoke-${tk.id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 border border-cs-blueprint/40 bg-cs-blueprint/5 rounded-md p-3 text-[11px] text-zinc-700 font-mono" data-testid="api-v2-usage">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="text-zinc-900 font-semibold">{t("api_v2_title")}</div>
          <a
            href={`${window.location.origin}/api/docs`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-cs-blueprint hover:underline font-semibold"
            data-testid="api-v2-docs-link"
          >
            <ExternalLink size={11} /> {t("api_v2_docs_link")}
          </a>
        </div>
        <div>1. Power BI Desktop → Get Data → Web → Advanced</div>
        <div>2. URL: <code className="bg-white px-1 rounded">{`${window.location.origin}/api/v2/tabular/tasks`}</code></div>
        <div>3. Header <code className="bg-white px-1 rounded">X-API-Key</code> = <code className="bg-white px-1 rounded">cs_api_...</code></div>
        <div className="mt-1.5 text-zinc-500">
          {t("api_v2_resources")}: /api/v2/projects · /api/v2/versions · /api/v2/tasks · /api/v2/restrictions · /api/v2/ai-analyses · /api/v2/work-program
        </div>
        <div className="text-zinc-500">{t("api_v2_schema_hint")}: <code className="bg-white px-1 rounded">/api/v2/schema</code></div>
      </div>

      <div className="mt-2 border border-amber-300 bg-amber-50 rounded-md p-3 text-[11px] text-amber-900 font-mono" data-testid="api-v1-deprecated">
        <div className="font-semibold inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> {t("api_v1_deprecated_title")}
        </div>
        <div className="mt-1">{t("api_v1_deprecated_help")}</div>
        <div className="mt-1 text-amber-800">/api/v1/data/projects · /api/v1/data/syncs · /api/v1/data/tasks — Header Authorization: Bearer cs_api_...</div>
      </div>
    </div>
  );
}

/**
 * MaintenanceCard — admin-only utilities for repairing tenant data after
 * a deploy or data migration. Currently exposes a "Recompute task ordering"
 * button that re-numbers `seq` on every task using the same tree-DFS
 * algorithm the gantt endpoints use at read-time.
 *
 * Visible to company-admin (scope = active company) and super-admin
 * (additional "all companies" option). Both are idempotent.
 */
function MaintenanceCard({ isSuperAdmin }) {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runRecompute = async (allCompanies = false) => {
    if (running) return;
    if (!window.confirm(allCompanies ? t("maint_confirm_all") : t("maint_confirm_company"))) return;
    setRunning(true);
    setLastResult(null);
    try {
      const r = await recomputeTaskSeq(allCompanies ? { all_companies: true } : {});
      setLastResult(r);
      toast.success(t("maint_done", { syncs: r.processed_syncs, tasks: r.updated_tasks }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="settings-maintenance-card">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <Wrench size={18} className="text-cs-accent" />
        <div>
          <h3 className="font-heading text-base font-semibold">{t("maint_title")}</h3>
          <p className="text-[11px] text-zinc-500 font-mono">{t("maint_subtitle")}</p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="border border-zinc-200 rounded-md p-3">
          <div className="text-sm font-semibold text-zinc-900">{t("maint_recompute_title")}</div>
          <p className="text-xs text-zinc-600 mt-1">{t("maint_recompute_desc")}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              onClick={() => runRecompute(false)}
              disabled={running}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-60"
              data-testid="maint-recompute-company-btn"
            >
              {running ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
              {t("maint_recompute_company_btn")}
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => runRecompute(true)}
                disabled={running}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-800 text-xs font-semibold rounded-md hover:bg-amber-50 disabled:opacity-60"
                data-testid="maint-recompute-all-btn"
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                {t("maint_recompute_all_btn")}
              </button>
            )}
            {lastResult && (
              <span className="font-mono text-[11px] text-zinc-500 ml-2" data-testid="maint-recompute-result">
                {lastResult.processed_syncs} syncs · {lastResult.updated_tasks} tasks
              </span>
            )}
          </div>
        </div>

        <MppParserStatus />
      </div>
    </div>
  );
}

/**
 * MppParserStatus — surfaces the MPP/JVM status from /api/health so admins can
 * see at-a-glance whether `.mpp` uploads will work. If unavailable, a button
 * triggers a runtime install + JVM boot via /api/_internal/init-mpxj.
 *
 * Both endpoints are intentionally public (no auth) and idempotent — they only
 * read or initialise infra state, never mutate tenant data.
 */
function MppParserStatus() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null); // { available, libjvm_path, error }
  const [loading, setLoading] = useState(true);
  const [reiniting, setReiniting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get("/health");
      setStatus(r.data?.mpp_parser || { available: false, error: "no payload" });
    } catch (e) {
      setStatus({ available: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const reinit = async () => {
    if (reiniting) return;
    setReiniting(true);
    try {
      const r = await http.post("/_internal/init-mpxj");
      const payload = r.data || {};
      setStatus({
        available: !!payload.available,
        libjvm_path: payload.libjvm_path,
        error: payload.error,
      });
      if (payload.available) toast.success(t("maint_mpp_reinit_ok"));
      else toast.error(t("maint_mpp_reinit_fail") + (payload.error ? `: ${payload.error}` : ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setReiniting(false);
    }
  };

  const available = status?.available === true;
  return (
    <div className="border border-zinc-200 rounded-md p-3" data-testid="maint-mpp-card">
      <div className="flex items-start gap-2">
        <FileBox size={14} className="text-zinc-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-900">{t("maint_mpp_title")}</span>
            {loading ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-zinc-400" data-testid="maint-mpp-status-loading">
                <Loader2 size={11} className="animate-spin" /> {t("maint_mpp_checking")}
              </span>
            ) : available ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-cs-success" data-testid="maint-mpp-status-ok">
                <CheckCircle2 size={11} /> {t("maint_mpp_available")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-red-600" data-testid="maint-mpp-status-fail">
                <AlertTriangle size={11} /> {t("maint_mpp_unavailable")}
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-1">{t("maint_mpp_desc")}</p>

          {status?.libjvm_path && (
            <div className="mt-2 text-[11px] font-mono text-zinc-500 truncate" data-testid="maint-mpp-libpath">
              {t("maint_mpp_libpath")}: <span className="text-zinc-700">{status.libjvm_path}</span>
            </div>
          )}
          {status?.heap_max && (
            <div className="mt-1 text-[11px] font-mono text-zinc-500" data-testid="maint-mpp-heap">
              {t("maint_mpp_heap")}: <span className="text-zinc-700">{status.heap_max}</span>
            </div>
          )}
          {status?.error && !available && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-[11px] font-mono text-red-700 break-words" data-testid="maint-mpp-error">
              {t("maint_mpp_error")}: {status.error}
            </div>
          )}

          {!available && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={reinit}
                disabled={reiniting || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-60"
                data-testid="maint-mpp-reinit-btn"
              >
                {reiniting ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {reiniting ? t("maint_mpp_reinit_running") : t("maint_mpp_reinit_btn")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
